import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  ThemeProvider, 
  createTheme, 
  CssBaseline, 
  Alert,
  Container,
  Button,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogContent,
  Tooltip
} from '@mui/material';
import api from './api'; // Import our custom axios instance
import FileUpload from './components/FileUpload';
import FileHistory from './components/FileHistory';
import DataGrid from './components/DataGrid';
import SearchFilters from './components/SearchFilters';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import SubscriptionGate from './components/SubscriptionGate';
import { useSubscriptionGuard } from './hooks/useSubscriptionGuard';
import AdminPanel from './components/AdminPanel';
import MyClaims from './components/MyClaims';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Header from './components/Header';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage'; // Using StorageIcon for database
import { AiAssistant, FullscreenAiAssistant } from './components/AiAssistant';
import ErrorBoundary from './components/ErrorBoundary';
import CompanyResearch from './components/CompanyResearch';
import CompanySearch from './components/CompanySearch';
import { LanguageProvider } from './context/LanguageContext';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import AddRecordButton from './components/AddRecordButton'; // Import the AddRecordButton component
import axios from 'axios';
import EventsDashboard from './components/EventsDashboard'; // Import the new EventsDashboard component
import DisclaimerDialog from './components/DisclaimerDialog';
import UserGuidance from './components/UserGuidance'; // Import the UserGuidance component
import UnifiedSearchSection from './components/UnifiedSearchSection'; // Import the new unified search component
import SalesOutreachShell from './components/SalesOutreachShell';
import IntegrationBanner from './components/IntegrationBanner'; // Import integration banner component
import logger from './utils/logger'; // Import secure logger
import useIsMobile from './hooks/useIsMobile';

// Debug environment variables (only in development)
logger.debug('Environment variables debug:');
logger.debug('NODE_ENV:', process.env.NODE_ENV);
logger.debug('REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
logger.debug('REACT_APP_ENVIRONMENT:', process.env.REACT_APP_ENVIRONMENT);

// Check if we're in development environment
const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';

// Create a theme based on environment
const theme = createTheme({
  palette: {
    primary: {
      main: isDevelopment ? '#ff9800' : '#1976d2', // Orange in dev, blue in production
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

// Environment and theme configured

// Global error handler to suppress ResizeObserver errors
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  console.error = function(msg, ...args) {
    if (typeof msg === 'string' && (
      msg.includes('ResizeObserver loop') ||
      msg.includes('You are providing a disabled `button` child to the Tooltip component')
    )) {
      // Don't log ResizeObserver or MUI Tooltip disabled button errors
      return;
    }
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      originalConsoleError.apply(console, [msg, ...args]);
    }
  };
}

// Main app content that uses the auth context
const AppContent = () => {
  const { user, loading: authLoading, isAdmin, isSupervisor } = useAuth();
  const { needsSubscription, isTrialExpired } = useSubscription();
  const { guardFeature } = useSubscriptionGuard();
  const isMobile = useIsMobile();
  const [fileUploaded, setFileUploaded] = useState(false);
  const [columns, setColumns] = useState([]);
  const [columnTypes, setColumnTypes] = useState({});
  const [uniqueValues, setUniqueValues] = useState({});
  const [columnsInfo, setColumnsInfo] = useState({ data_types: {}, unique_values: {} });
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchParams, setSearchParams] = useState({ filters: [], search: "", sortField: "", sortOrder: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetToken, setResetToken] = useState(() => {
    return new URLSearchParams(window.location.search).get('reset_token');
  });
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [showFullscreenAi, setShowFullscreenAi] = useState(false);
  const [activeTab, setActiveTab] = useState('data');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [aiAssistantMessages, setAiAssistantMessages] = useState([]);
  const [aiAssistantSettings, setAiAssistantSettings] = useState({
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 1000
  });
  const [currentThread, setCurrentThread] = useState(null);
  const [showCompanySearch, setShowCompanySearch] = useState(false);
  const [userProfileMenu, setUserProfileMenu] = useState(null);
  const [language, setLanguage] = useState('en');
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  
  // Unified search states
  const [salesOutreachOpen, setSalesOutreachOpen] = useState(false);
  const [searchRecord, setSearchRecord] = useState(null);
  const [contactSearchResult, setContactSearchResult] = useState(null);
  
  // If user is not logged in, show login form first (unless resetting password)
  React.useEffect(() => {
    if (resetToken) {
      return;
    }

    if (!authLoading && !user) {
      if (!showSignup && !showForgotPassword) {
        setShowLogin(true);
      }
    } else if (user) {
      setShowLogin(false);
      setShowSignup(false);
      setShowForgotPassword(false);
      // If user just logged in and data is already loaded, fetch it
      if (user && fileUploaded && !isMobile) {
        logger.log("User logged in and data is loaded, fetching data...");
        fetchData({ page: 1 });
      }
    }
  }, [user, authLoading, showSignup, showForgotPassword, resetToken]);

  // Check for previously uploaded file on component mount
  React.useEffect(() => {
    checkExistingData();
  }, []);

  // Check for login errors at app level in case the Login component doesn't catch them
  React.useEffect(() => {
    // Handle Microsoft login errors that might be in the URL
    const params = new URLSearchParams(window.location.search);
    if (params.has('error') && !showLogin) {
      logger.log("Detected login error in URL params:", params.get('error'));
      setShowLogin(true);
      // URL cleanup will be handled by the Login component
    }
  }, [window.location.search]);

  // Check for disclaimer acceptance
  React.useEffect(() => {
    if (user && !authLoading && !disclaimerChecked) {
      checkDisclaimerStatus();
    }
  }, [user, authLoading, disclaimerChecked]);

  // Function to check if there's a previously uploaded file
  const checkExistingData = async (options = {}) => {
    const { skipDataFetch = false, noReload = false } = options;
    
    setLoading(true);
    setError(null);
    
    try {
      // First check if database exists and has data
      try {
        const debugResponse = await api.get('/api/public/data-status', {
          params: {
            no_reload: noReload
          }
        });
        
        if (debugResponse.data.success && debugResponse.data.data_loaded) {
          logger.log(`Database data is already loaded (${debugResponse.data.record_count} records)`);
          setFileUploaded(true);
          
          // If user is logged in, fetch the data (skip on mobile — datatable is hidden)
          if (user && !skipDataFetch && !isMobile) {
            fetchData({ page: 1 });
          } else {
            setLoading(false);
            logger.log("Data is loaded but user is not logged in or skipDataFetch is set. Will fetch data after login.");
          }
          return debugResponse.data;
        } else {
          logger.log("Database exists but no data is loaded. Status:", debugResponse.data);
        }
      } catch (debugError) {
        logger.log("Could not check database status:", debugError);
      }
      
      // If user is not logged in, we can't check the data endpoint
      if (!user) {
        logger.log("User not logged in, can't check data endpoint");
        setLoading(false);
        return { success: false, reason: "not_logged_in" };
      }
      
      // Always try to load from database.db directly first if an admin
      if (isAdmin) {
        logger.log("Admin user detected - attempting to load from database.db");
        try {
          // Try direct database loading
          const loadResponse = await api.post('/api/load-from-database', {
            method: 'sqlite'
          });
          
          if (loadResponse.data.success) {
            logger.log("Successfully loaded data from database.db");
            setFileUploaded(true);
            if (!skipDataFetch && !isMobile) {
              fetchData({ page: 1 });
            } else {
              setLoading(false);
            }
            return { success: true, from: "database_load" };
          } else {
            logger.log("Failed to load from database directly:", loadResponse.data.message);
          }
        } catch (loadError) {
          logger.error("Error trying to load from database:", loadError);
        }
      } else {
        logger.log("User is not an admin, cannot load from database");
      }
      
      // Next check if data is already available in the backend
      try {
        const response = await api.get('/data', {
          params: {
            page: 1,
            limit: 10
          }
        });

        if (response.data.success && response.data.data && response.data.data.length > 0) {
          // Data is already loaded on the server
          logger.log("Data already loaded on server, fetching data...");
          setFileUploaded(true);
          if (!skipDataFetch && !isMobile) {
            fetchData({ page: 1 });
          } else {
            setLoading(false);
          }
          return { success: true, from: "server_data" };
        }
      } catch (error) {
        logger.error("Error checking data endpoint:", error);
      }

      // If we reach here, no data was found or loaded
      setLoading(false);
      return { success: false, reason: "no_data_found" };
      
    } catch (error) {
      logger.error("Error in checkExistingData:", error);
      setLoading(false);
      return { success: false, error };
    }
  };

  const handleFileUpload = async (file, customFormData) => {
    if (!file) {
      setError('No file selected');
      return;
    }

    setLoading(true);
    setError(null);
    setFileUploadProgress(0);

    try {
      // Use the provided custom FormData if available, otherwise create a new one
      const formData = customFormData || new FormData();
      
      // If we're using a new FormData (not the custom one), add the file
      if (!customFormData) {
        formData.append('file', file);
      }

      // Create a specific API instance just for this file upload with a longer timeout
      const uploadApi = axios.create({
        baseURL: api.defaults.baseURL,
        timeout: 1200000, // 20 minute timeout for very large files
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      // Send the file to the server with the dedicated upload API instance
      const response = await uploadApi.post('/api/files/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setFileUploadProgress(percentCompleted);
          // Reset timeout on progress to prevent timeout during large uploads
          uploadApi.defaults.timeout = 1200000; // Reset the 20 minute timeout on each progress event
        },
      });

      // Process the response
      if (response.data.success) {
        // Include information about claimed records if available
        const claimMessage = response.data.claimed_count ? 
          ` ${response.data.claimed_count} records claimed for ${response.data.claimed_for_user}.` : '';
          
        setSuccess(`File ${file.name} uploaded successfully!${claimMessage} ${response.data.new_records} new records added, ${response.data.updated_records} records updated.`);
        setFileUploaded(true);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        
        // Fetch data after successful upload
        setTimeout(() => {
          fetchData({ page: 1 });
        }, 500);
      } else {
        throw new Error('Server returned an unsuccessful response');
      }
    } catch (error) {
      logger.error('Error uploading file:', error);
      
      // Enhanced error logging
      if (error.response) {
        logger.error('Server error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
        
        // Try to extract more meaningful error message
        const errorMessage = 
          error.response.data?.message || 
          `Server error: ${error.response.status} ${error.response.statusText}`;
        
        setError(errorMessage);
      } else if (error.request) {
        // Request was made but no response received
        logger.error('No response received:', {
          timeout: error.request.timeout,
          responseType: error.request.responseType,
          readyState: error.request.readyState,
          status: error.request.status
        });
        
        setError('No response received from server. The request may have timed out.');
      } else {
        // Set a user-friendly error message
        setError(error.message || 'An error occurred while uploading the file.');
      }
      
      setFileUploaded(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (params = {}) => {
    const {
      page = currentPage,
      searchTerm = searchParams.search,
      sortField = searchParams.sortField,
      sortOrder = searchParams.sortOrder,
      filters = searchParams.filters,
      isFileUpload = false,
      forceRefresh = false // Add parameter to force a full refresh
    } = params;

    setLoading(true);
    setError(null);

    try {
      // Construct the endpoint URL - use /search endpoint for user-specific data
      const endpoint = '/search';
      
      // Check if we have any outreach status filters
      const hasOutreachFilters = filters.some(f => f.field?.startsWith('outreach_status'));
      
      // Log filter information before making the request
      logger.log("Fetching data with params:", {
        page,
        pageSize,
        search: searchTerm,
        sortField,
        sortOrder,
        hasOutreachFilters,
        filters: filters.map(f => ({ field: f.field, operator: f.operator, value: f.value })),
        forceRefresh
      });
      
      // Make the request
      const response = await api.post(endpoint, {
        page,
        pageSize,
        search: searchTerm,
        sortField,
        sortOrder,
        filters,
        // Add a flag to ensure we get the full count during refresh
        get_total_count: true,
        refresh_cache: forceRefresh,
        // Add a flag to include outreach status data
        include_outreach: true
      });

      if (response.data.success) {
        // Add debugging for outreach status data
        const outreachCount = response.data.data.filter(item => 
          item.outreach_status && item.outreach_status.has_outreach === true
        ).length;
        
        logger.log(`Fetched data with ${outreachCount} records having outreach status`);
        
        // Outreach filters applied and verified
        
        // Update state with the fetched data
        setData(response.data.data);
        setColumns(response.data.columns);
        
        // Use 'total' which is the filtered count (after filters are applied)
        // 'total_records' is the total in DB before filters, which we don't want for display
        if (response.data.total !== undefined && response.data.total !== null) {
          logger.log("Filtered total records from backend:", response.data.total, "(total in DB:", response.data.total_records, ")");
          setTotalRows(response.data.total);
        } else if (response.data.total_records) {
          // Fallback to total_records only if total is not available
          logger.warn("Using total_records as fallback (total not available):", response.data.total_records);
          setTotalRows(response.data.total_records);
        } else {
          setTotalRows(0);
        }
        
        setTotalPages(response.data.total_pages);
        
        // Update columnsInfo if it's returned
        if (response.data.columns_info) {
          setColumnsInfo(response.data.columns_info);
        }
        
        logger.log(`Fetched ${response.data.data.length} records, total: ${response.data.total_records || response.data.total}`);
      } else {
        throw new Error(response.data.message || 'Failed to fetch data');
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
      
      // Handle axios error details
      if (error.response) {
        logger.error('Server error details:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      
      setError(error.message || 'An error occurred while fetching data.');
      
      // Reset to empty data on error
      setData([]);
      setColumns([]);
      setTotalRows(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (newParams) => {
    
    // Create a new params object with search term
    const searchParams = {
      // Default to page 1 when searching
      page: 1,
      // Extract search term if it exists in the newParams
      searchTerm: newParams.search || '',
      // Extract sort field and order if they exist
      sortField: newParams.sort?.field || '',
      sortOrder: newParams.sort?.direction || 'asc',
      // Keep filters
      filters: newParams.filters || []
    };
    
    // Update search params state
    setSearchParams({
      search: searchParams.searchTerm,
      sortField: searchParams.sortField,
      sortOrder: searchParams.sortOrder,
      filters: searchParams.filters
    });
    
    // Fetch data with the new params
    fetchData(searchParams);
  };

  const handleSort = (field) => {
    const isAsc = searchParams.sortField === field && searchParams.sortOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSearchParams(prev => ({ ...prev, sortField: field, sortOrder: newOrder }));
    fetchData({ page: 1, sortField: field, sortOrder: newOrder });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchData({ page: newPage });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    fetchData({ page: 1 });
  };

  // Handle user login success
  const handleLoginSuccess = () => {
    logger.log("Login success handler triggered");
    setShowLogin(false);
    setShowSignup(false);
    checkExistingData();
  };

  // Handle switching to signup
  const handleSwitchToSignup = () => {
    logger.log("Switching to signup");
    setShowLogin(false);
    setShowSignup(true);
  };

  // Handle switching to login
  const handleSwitchToLogin = () => {
    logger.log("Switching to login");
    setShowSignup(false);
    setShowForgotPassword(false);
    setShowLogin(true);
  };

  const handleSwitchToForgotPassword = () => {
    setShowLogin(false);
    setShowSignup(false);
    setShowForgotPassword(true);
  };

  const handleResetSuccess = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('reset_token');
    const nextQuery = params.toString();
    window.history.replaceState({}, document.title, `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`);
    setResetToken(null);
    setShowForgotPassword(false);
    setShowLogin(true);
    setSuccess('Password updated successfully. You can now sign in.');
  };

  // Handle signup success
  const handleSignupSuccess = (result) => {
    logger.log("Signup success handler triggered");
    setShowSignup(false);
    setShowLogin(true);
    setSuccess(result.message);
  };

  // Effect to initialize column info whenever it's updated
  useEffect(() => {
    if (columnsInfo && columnsInfo.data_types) {
      setColumnTypes(columnsInfo.data_types);
    }
    if (columnsInfo && columnsInfo.unique_values) {
      setUniqueValues(columnsInfo.unique_values);
    }
  }, [columnsInfo]);

  // Keep track of when records are selected
  useEffect(() => {
    // Keep AI assistant state in sync with selection
    if (selectedRecords.length === 0) {
      setShowFullscreenAi(false);
    }
  }, [selectedRecords]);

  // Compute selected records data for AI assistant
  const getSelectedRecordsData = () => {
    if (!data || !Array.isArray(data) || data.length === 0 || !selectedRecords || selectedRecords.length === 0) {
      return [];
    }
    
    return selectedRecords
      .map(id => data.find(row => row.record_id === id))
      .filter(Boolean); // Remove undefined entries
  };

  // Handler for toggling fullscreen mode
  const handleFullscreenToggle = useCallback((isFullscreen) => {
    setShowFullscreenAi(isFullscreen);
  }, []);
  
  // Handler for closing fullscreen mode
  const handleCloseFullscreen = useCallback(() => {
    setShowFullscreenAi(false);
  }, []);
  
  // Since the AI assistant is now embedded in the layout, this handler is simplified
  const handleCloseAiAssistant = useCallback(() => {
    // Instead of hiding the AI assistant, just clear the selection
    setSelectedRecords([]);
  }, []);

  // Simple handler for opening the company search dialog
  const handleOpenCompanySearch = useCallback(() => {
    setShowCompanySearch(true);
  }, []);
  
  // Simple handler for closing the company search dialog
  const handleCloseCompanySearch = useCallback(() => {
    setShowCompanySearch(false);
  }, []);

  // Function to manually load data from database.db
  const handleLoadFromDatabase = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First check the current data status
      try {
        const currentDataResponse = await api.get('/api/debug/current-data');
        logger.log("Current data status:", currentDataResponse.data);
        
        if (currentDataResponse.data.has_data && currentDataResponse.data.record_count > 0) {
          logger.log(`Data already loaded (${currentDataResponse.data.record_count} records)`);
          setSuccess(`Data already loaded (${currentDataResponse.data.record_count} records)`);
          setFileUploaded(true);
          fetchData({ page: 1 });
          setLoading(false);
          return;
        } else {
          logger.log("No data currently loaded, will try direct loading methods");
        }
      } catch (currentDataError) {
        logger.error("Error checking current data:", currentDataError);
      }
      
      // Try all methods to load the data
      let loadSuccess = false;
      
      // Method 1: Direct SQLite loading (preferred)
      try {
        logger.log("Trying SQLite direct loading method...");
        const response = await api.post('/api/load-from-database', {
          method: 'sqlite'
        });
        
        if (response.data.success) {
          logger.log("SQLite loading successful:", response.data);
          setSuccess(response.data.message || `Successfully loaded ${response.data.record_count} records using SQLite`);
          loadSuccess = true;
        } else {
          logger.log("SQLite loading failed:", response.data.message);
        }
      } catch (error) {
        logger.error("Error with SQLite loading:", error);
      }
      
      // Method 2: Try SQLAlchemy if SQLite failed
      if (!loadSuccess) {
        try {
          logger.log("Trying SQLAlchemy loading method...");
          const response = await api.post('/api/load-from-database', {
            method: 'sqlalchemy'
          });
          
          if (response.data.success) {
            logger.log("SQLAlchemy loading successful:", response.data);
            setSuccess(response.data.message || `Successfully loaded ${response.data.record_count} records using SQLAlchemy`);
            loadSuccess = true;
          } else {
            logger.log("SQLAlchemy loading failed:", response.data.message);
          }
        } catch (error) {
          logger.error("Error with SQLAlchemy loading:", error);
        }
      }
      
      // Method 3: Try direct inspection
      if (!loadSuccess) {
        try {
          logger.log("Trying table inspection method...");
          const response = await api.get('/api/debug/inspect-all-tables');
          
          if (response.data.success && response.data.data_found) {
            logger.log("Table inspection found data:", response.data);
            setSuccess(`Found data in tables: ${response.data.tables.filter(t => t.row_count > 0).map(t => t.name).join(', ')}`);
            
            // Try again with the new information
            const loadResponse = await api.post('/api/load-from-database', {
              method: 'auto',
              table: response.data.tables.find(t => t.row_count > 0)?.name
            });
            
            if (loadResponse.data.success) {
              logger.log("Loading after inspection successful:", loadResponse.data);
              setSuccess(loadResponse.data.message || `Successfully loaded data after inspection`);
              loadSuccess = true;
            }
          } else {
            logger.log("No data found in table inspection");
            setError("No data found in any database tables. Please upload a file first.");
          }
        } catch (error) {
          logger.error("Error with table inspection:", error);
        }
      }
      
      if (loadSuccess) {
        setFileUploaded(true);
        fetchData({ page: 1 });
      } else {
        setError("Failed to load data from database using all available methods.");
      }
    } catch (error) {
      logger.error("Error in handleLoadFromDatabase:", error);
      setError(error.message || "An error occurred while loading from database");
    } finally {
      setLoading(false);
    }
  };

  // Function to create a sample database structure for testing
  const handleCreateSampleDb = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/debug/create-sample-db');
      
      if (response.data.success) {
        setSuccess(response.data.message || "Created sample database with test data");
        if (response.data.data_loaded) {
          setFileUploaded(true);
          fetchData({ page: 1 });
        } else {
          setError("Sample database created but couldn't load the data. Try clicking 'Load from Database.db'");
        }
      } else {
        setError(response.data.message || "Failed to create sample database");
      }
    } catch (error) {
      logger.error("Error creating sample database:", error);
      setError("Error creating sample database: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to check current data status
  const handleCheckCurrentData = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/api/debug/current-data');
      logger.log("Current data status:", response.data);
      
      if (response.data.has_data && response.data.record_count > 0) {
        setSuccess(`Data is already loaded: ${response.data.record_count} records with ${response.data.column_count} columns.`);
        setFileUploaded(true);
        fetchData({ page: 1 });
      } else {
        setError(`No data currently loaded (${response.data.record_count} records). Try using 'Load from Database.db'.`);
      }
    } catch (error) {
      logger.error("Error checking current data:", error);
      setError("Error checking current data status: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to inspect all tables in the database
  const handleInspectTables = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/api/debug/inspect-all-tables');
      logger.log("Database table inspection:", response.data);
      
      if (response.data.success) {
        if (response.data.data_found) {
          // Found tables with data
          const tablesWithData = response.data.tables.filter(table => table.row_count > 0);
          const tableInfo = tablesWithData.map(table => 
            `${table.name}: ${table.row_count} rows (${table.has_json_data ? 'contains JSON data' : 'no JSON data'})`
          ).join('\n');
          
          setSuccess(`Found ${tablesWithData.length} tables with data:\n${tableInfo}`);
          
          // Try to load data directly if we found tables with data
          const loadResponse = await api.post('/api/load-from-database', {
            method: 'sqlite'
          });
          
          if (loadResponse.data.success) {
            setSuccess(`Successfully loaded ${loadResponse.data.record_count} records from database`);
            setFileUploaded(true);
            fetchData({ page: 1 });
          } else {
            setError(`Found tables with data but couldn't load them: ${loadResponse.data.message}`);
          }
        } else {
          setError(`Database exists (${response.data.database_size_mb.toFixed(2)} MB) but no tables contain data.`);
        }
      } else {
        setError(`Error inspecting database: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      logger.error("Error inspecting tables:", error);
      setError("Error inspecting database tables: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to check if user has accepted the disclaimer
  const checkDisclaimerStatus = async () => {
    try {
      const response = await api.get('/check-disclaimer');
      setDisclaimerChecked(true);
      
      if (response.data.success && !response.data.accepted) {
        setShowDisclaimer(true);
      }
    } catch (error) {
      logger.error('Error checking disclaimer status:', error);
    }
  };
  
  // Handle disclaimer acceptance
  const handleDisclaimerAccept = () => {
    setShowDisclaimer(false);
    // Refresh data after disclaimer is accepted
    if (fileUploaded) {
      fetchData({ page: 1 });
    }
  };
  
  // Handle disclaimer dialog close (can't close without accepting)
  const handleDisclaimerClose = () => {
    // Empty function as user can't close without accepting
  };

  // Unified search handlers
  const handleDomainSearch = (record) => {
    setSearchRecord(record);
    setSalesOutreachOpen(true);
  };

  const handleContactSearchResult = (result) => {
    setContactSearchResult(result);
    // You can add additional logic here to display the contact result
    logger.log('Contact search completed:', result);
  };

  const handleCloseSalesOutreach = () => {
    setSalesOutreachOpen(false);
    setSearchRecord(null);
  };

  const handleNavigateHome = () => {
    setSalesOutreachOpen(false);
    setSearchRecord(null);
    setContactSearchResult(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (resetToken) {
    return (
      <ErrorBoundary>
        <ResetPassword token={resetToken} onResetSuccess={handleResetSuccess} />
      </ErrorBoundary>
    );
  }

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (showForgotPassword) {
    return (
      <ErrorBoundary>
        <ForgotPassword
          onBackToLogin={handleSwitchToLogin}
        />
      </ErrorBoundary>
    );
  }

  if (showLogin) {
    return (
      <ErrorBoundary>
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, mx: 2, mt: 2 }}>
            {success}
          </Alert>
        )}
        <Login 
          onLoginSuccess={handleLoginSuccess} 
          onSwitchToSignup={handleSwitchToSignup}
          onForgotPassword={handleSwitchToForgotPassword}
        />
      </ErrorBoundary>
    );
  }

  if (showSignup) {
    return (
      <ErrorBoundary>
        <Signup 
          onSignupSuccess={handleSignupSuccess} 
          onSwitchToLogin={handleSwitchToLogin}
        />
      </ErrorBoundary>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 4 }, mb: { xs: 1, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Header onNavigateHome={handleNavigateHome} />
      
      {/* Integration Banner - shows when user has active subscription but no add-ons */}
      {user && <IntegrationBanner />}
      
      {/* Show EventsDashboard if user is logged in (desktop only) */}
      {user && !isMobile && (
        <EventsDashboard daysAhead={7} />
      )}
      
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* File upload section */}
      {!user ? (
        <Typography variant="h6" sx={{ textAlign: 'center', py: 4 }}>
          Please log in to access the application.
        </Typography>
      ) : (
        <>
          {/* Admin Panel is now moved to the user menu, no need to render it here */}
          
          {/* Unified Search Section */}
          <UnifiedSearchSection
            onDomainSearch={handleDomainSearch}
            onContactSearchResult={handleContactSearchResult}
            loading={loading}
          />
          
          {/* Search filters and datatable — desktop only */}
          {!isMobile && (
            <>
              <Box sx={{ mb: 3 }}>
                <SubscriptionGate 
                  featureName="search and filtering"
                  showPreview={true}
                  previewMessage="Advanced search and filtering requires an active subscription"
                >
                  <SearchFilters
                    columns={columns}
                    columnTypes={columnTypes}
                    uniqueValues={uniqueValues}
                    onSearch={handleSearch}
                    loading={loading}
                  />
                </SubscriptionGate>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <Box sx={{ 
                  flexGrow: 1,
                  minWidth: 0
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" component="h1" sx={{ mb: 0 }}>
                      Outreach Database
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {user && (
                        <SubscriptionGate 
                          featureName="adding new records"
                          showPreview={false}
                        >
                          <AddRecordButton onRecordAdded={() => fetchData({ page: 1 })} />
                        </SubscriptionGate>
                      )}
                    </Box>
                  </Box>

                  <SubscriptionGate 
                    featureName="the outreach database"
                    showPreview={true}
                    previewMessage="Access to the outreach database requires an active subscription"
                  >
                    <DataGrid
                      columns={columns}
                      data={data}
                      loading={loading}
                      error={error}
                      totalRows={totalRows}
                      currentPage={currentPage}
                      pageSize={pageSize}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      onDataChange={async () => {
                        logger.log("DataGrid onDataChange triggered - refreshing data");
                        try {
                          await fetchData({
                            page: currentPage,
                            pageSize: pageSize,
                            searchTerm: searchParams.search,
                            sortField: searchParams.sortField,
                            sortOrder: searchParams.sortOrder,
                            filters: searchParams.filters,
                            forceRefresh: true
                          });
                          logger.log("Data refresh completed, new total rows:", totalRows);
                          return totalRows;
                        } catch (error) {
                          logger.error("Error refreshing data:", error);
                          throw error;
                        }
                      }}
                      onSelectionChange={setSelectedRecords}
                      selectedRecords={selectedRecords}
                      searchTerm={searchParams.search}
                      onSort={handleSort}
                      searchParams={{
                        search: searchParams.search,
                        filters: searchParams.filters,
                        sortField: searchParams.sortField,
                        sortOrder: searchParams.sortOrder
                      }}
                    />
                  </SubscriptionGate>
                </Box>
              </Box>
            </>
          )}
        </>
      )}
      
      {/* AI Assistant fullscreen mode - removed to completely disable */}
      
      {/* Company Search Dialog */}
      <Dialog
        open={showCompanySearch}
        onClose={handleCloseCompanySearch}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <SubscriptionGate 
            featureName="company search"
            showPreview={false}
          >
            <CompanySearch onClose={handleCloseCompanySearch} />
          </SubscriptionGate>
        </DialogContent>
      </Dialog>
      
      {/* SalesOutreach for domain search results */}
      {searchRecord && (
        <SalesOutreachShell
          open={salesOutreachOpen}
          onClose={handleCloseSalesOutreach}
          recordId={searchRecord.record_id}
          companyName={searchRecord.Instellingsnaam || searchRecord.companyName}
          contactInfo={searchRecord}
          openWithUrlSearch={true}
        />
      )}
      
      {/* Add disclaimer dialog */}
      <DisclaimerDialog
        open={showDisclaimer}
        onClose={handleDisclaimerClose}
        onAccept={handleDisclaimerAccept}
      />
      
      {/* User Guidance for new users */}
      {user && disclaimerChecked && !showSubscriptionModal && (
        <UserGuidance />
      )}
      
      {/* Subscription enforcement is now handled globally by AuthContext to avoid duplicate modals */}
    </Container>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SubscriptionProvider>
          <AppContent />
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 